// Typed client for the admin property-review moderation surface
// (PROP-77). Five endpoints under /admin/property-reviews — the list
// + the three POST mutations. Wire shapes match
// backend/internal/reviews/store.go and the payload returned by the
// adminapi handler in property_reviews.go field-for-field; keep in
// sync.

import { api } from "./api";

export type ModerationFilter = "reported" | "hidden" | "all";

export interface ReviewReportEntry {
  reporter_user_id: string;
  reporter_name: string;
  reason: string;
  note?: string;
  created_at: string;
}

export interface ModerationReview {
  id: string;
  project_name: string;
  author_user_id: string;
  author_name: string;
  rating: number;
  body: string;
  status: "visible" | "hidden" | "deleted";
  report_count: number;
  created_at: string;
  updated_at: string;
  reports: ReviewReportEntry[];
}

export interface ModerationListResponse {
  reviews: ModerationReview[] | null;
  filter: ModerationFilter;
  next_cursor?: string;
}

export interface ModerationActionResponse {
  review: ModerationReview;
  audit_logged: boolean;
}

// listModerationReviews fetches one page. limit/cursor are optional —
// omit for the server defaults. Normalises the server's `null` for
// "no rows" so the caller can always `.map`.
export async function listModerationReviews(opts: {
  filter?: ModerationFilter;
  limit?: number;
  cursor?: string;
} = {}): Promise<ModerationListResponse> {
  const params: string[] = [];
  if (opts.filter) params.push(`filter=${encodeURIComponent(opts.filter)}`);
  if (opts.limit) params.push(`limit=${opts.limit}`);
  if (opts.cursor) params.push(`cursor=${encodeURIComponent(opts.cursor)}`);
  const q = params.length ? `?${params.join("&")}` : "";
  const resp = await api.get<ModerationListResponse>(`/admin/property-reviews${q}`);
  return { ...resp, reviews: resp.reviews ?? [] };
}

function moderationAction(
  id: string,
  verb: "hide" | "restore" | "delete",
  reason: string,
): Promise<ModerationActionResponse> {
  return api.post<ModerationActionResponse>(
    `/admin/property-reviews/${encodeURIComponent(id)}/${verb}`,
    { reason },
  );
}

// hideReview sets status='hidden'; the reason is mandatory and lands
// verbatim in admin_audit_log (along with the before/after status and
// the row's project + author context).
export const hideReview = (id: string, reason: string) => moderationAction(id, "hide", reason);

// restoreReview sets status='visible' — typically after a "reported"
// row was hidden in error.
export const restoreReview = (id: string, reason: string) => moderationAction(id, "restore", reason);

// deleteReviewAsAdmin sets status='deleted'. Distinct from the author's
// own soft-delete (PROP-74): admin-side delete writes an audit row so
// the moderator's decision and reason are recoverable.
export const deleteReviewAsAdmin = (id: string, reason: string) => moderationAction(id, "delete", reason);
